/**
 * TeckiA - Créateur de Vidéos Publicitaires GROK IMAGINE PRO
 * Mode PRO par défaut avec prompts automatiques OU mode personnalisé
 * 2 SEGMENTS SEULEMENT (10s final)
 * ✅ LIMITE: 50 vidéos stockées maximum
 */

import React, { useState, useEffect } from 'react';
import { 
  Upload, Video, Sparkles, Clock, CheckCircle, AlertCircle, 
  Download, Play, Trash2, ArrowLeft, Package, Wand2, Film,
  DollarSign, Building, MessageSquare, User, XCircle, Archive
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../../config';
import CreditsPurchase from './CreditsPurchase';
import CreditsBanner from '../shared/CreditsBanner';

const VideoAdCreatorPro = ({ onBack }) => {
  // ========== MAIN TABS ==========
  const [activeMainTab, setActiveMainTab] = useState('create');
  
  // ========== CRÉATION VIDÉO ==========
  const [step, setStep] = useState(1);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [view, setView] = useState('studio');
  
  // ✅ DONNÉES PRODUIT
  const [productName, setProductName] = useState('');
  const [description, setDescription] = useState('');
  
  // ✅ MODE PERSONNALISÉ OU PRO AUTO
  const [customMode, setCustomMode] = useState(false);
  
  // ✅ DONNÉES POUR MODE PRO (seulement si customMode = false)
  const [gender, setGender] = useState('any');
  const [price, setPrice] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [slogan, setSlogan] = useState('');
  
  // ✅ DONNÉES POUR MODE PERSONNALISÉ (seulement si customMode = true)
  const [grokImages, setGrokImages] = useState([null, null]);
  const [grokImagePreviews, setGrokImagePreviews] = useState([null, null]);
  const [grokPrompts, setGrokPrompts] = useState(['', '']);
  
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // ========== MES VIDÉOS ==========
  const [standardVideos, setStandardVideos] = useState([]);
  const [grokVideos, setGrokVideos] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loadingVideos, setLoadingVideos] = useState(false);
  
  // ✅ NOUVEAU: Limite de stockage
  const MAX_STORED_VIDEOS = 50;

  useEffect(() => {
    if (activeMainTab === 'my-videos') {
      loadMyVideos();
      loadQuota();
    }
  }, [activeMainTab]);

  const loadMyVideos = async () => {
    setLoadingVideos(true);
    try {
      // ✅ UTILISER L'ENDPOINT UNIFIÉ
      const response = await axios.get(`${API_URL}/api/unified/my-creations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
  
      console.log('📦 Vidéos reçues:', response.data);
  
      // ✅ FILTRER LES VIDÉOS GROK
      const allVideos = response.data.videos || [];
      const grokVideosFiltered = allVideos.filter(v => v.type === 'grok' || v.service === 'pub_videos');
      
      console.log('🎥 Vidéos Grok trouvées:', grokVideosFiltered.length);
  
      setGrokVideos(grokVideosFiltered);
      setStandardVideos([]); // Pas de vidéos standard dans ce service
  
    } catch (err) {
      console.error('❌ Erreur chargement vidéos:', err);
      setGrokVideos([]);
      setStandardVideos([]);
    } finally {
      setLoadingVideos(false);
    }
  };

  const loadQuota = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/video-ads/quota`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (response.data.success || response.data.has_subscription !== undefined) {
        setQuota(response.data);
      }
    } catch (err) {
      console.error('Erreur chargement quota:', err);
      setQuota({
        success: false,
        has_subscription: false,
        max_videos_standard: 0,
        max_videos_grok: 0,
        used_videos_standard: 0,
        used_videos_grok: 0,
        remaining_standard: 0,
        remaining_grok: 0
      });
    }
  };

  const deleteVideoFromList = async (service, jobId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) return;

    try {
      await axios.delete(`${API_URL}/api/video-ads/delete/${service}/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      loadMyVideos();
      loadQuota();
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image trop grande (max 10MB)');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    
    const newImages = [...grokImages];
    newImages[0] = file;
    setGrokImages(newImages);
    
    const newPreviews = [...grokImagePreviews];
    newPreviews[0] = URL.createObjectURL(file);
    setGrokImagePreviews(newPreviews);
    
    setError(null);
  };

  const handleGrokImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('Image trop grande (max 10MB)');
      return;
    }

    const newImages = [...grokImages];
    newImages[index] = file;
    setGrokImages(newImages);

    const newPreviews = [...grokImagePreviews];
    newPreviews[index] = URL.createObjectURL(file);
    setGrokImagePreviews(newPreviews);
    
    setError(null);
  };

  // ✅ VÉRIFIER LIMITE STOCKAGE AVANT GÉNÉRATION
  const checkStorageLimit = () => {
    const totalStored = standardVideos.length + grokVideos.length;
    return totalStored < MAX_STORED_VIDEOS;
  };

  const generateVideo = async () => {
    if (!productName) {
      setError('Veuillez entrer le nom du produit');
      return;
    }
  
    if (customMode) {
      if (!grokPrompts[0] || !grokPrompts[1]) {
        setError('Veuillez remplir les 2 prompts personnalisés');
        return;
      }
    } else {
      if (!description) {
        setError('Veuillez entrer une description du produit');
        return;
      }
    }
    
    // ✅ VÉRIFIER LIMITE STOCKAGE
    if (!checkStorageLimit()) {
      setError(`⚠️ Limite de stockage atteinte ! Vous avez ${standardVideos.length + grokVideos.length} vidéos stockées (max ${MAX_STORED_VIDEOS}). Veuillez supprimer des anciennes vidéos avant d'en créer de nouvelles.`);
      return;
    }
  
    setLoading(true);
    setError(null);
  
    try {
      const formData = new FormData();
      formData.append('user_id', localStorage.getItem('user_id') || 'client');
      formData.append('product_name', productName);
      formData.append('custom_mode', customMode ? 'true' : 'false');
  
      if (customMode) {
        console.log('🎨 Mode personnalisé activé');
        
        formData.append('description', description || '');
        
        const image1ToUse = grokImages[0] || imageFile;
        formData.append('image1', image1ToUse);
        console.log('📸 Segment 1 image:', grokImages[0] ? 'Image spécifique uploadée' : 'Image principale réutilisée');
        
        const image2ToUse = grokImages[1] || imageFile;
        formData.append('image2', image2ToUse);
        console.log('📸 Segment 2 image:', grokImages[1] ? 'Image spécifique uploadée' : 'Image principale réutilisée');
        
        formData.append('prompt1', grokPrompts[0]);
        formData.append('prompt2', grokPrompts[1]);
        
      } else {
        console.log('🚀 Mode PRO Auto activé');
        
        formData.append('description', description);
        formData.append('gender', gender);
        formData.append('price', price);
        formData.append('company_name', companyName);
        formData.append('slogan', slogan);
        
        formData.append('image1', imageFile);
        formData.append('image2', imageFile);
        console.log('📸 Segments 1 & 2: même image principale utilisée');
      }
  
      const response = await axios.post(`${API_URL}/api/grok/generate-multi`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
  
      console.log('✅ Job créé:', response.data);
  
      setStep(3);
      startStatusPolling(response.data.job_id);
  
    } catch (err) {
      console.error('Erreur génération:', err);
      setError(err.response?.data?.error || 'Erreur lors de la génération');
      setLoading(false);
    }
  };

  const startStatusPolling = (jobId) => {
    console.log('🔄 Starting polling for job:', jobId);
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/api/grok/status/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        });
        
        console.log('📊 Status:', response.data);
        
        let progress = 10;
        let currentStep = 'Initialisation...';
        
        if (response.data.status === 'processing') {
          const segmentsCompleted = response.data.segments_completed || 0;
          const segmentsTotal = response.data.segments_total || 2;
          
          if (segmentsCompleted < segmentsTotal) {
            progress = 10 + (segmentsCompleted / segmentsTotal) * 70;
            currentStep = `Génération segment ${segmentsCompleted + 1}/${segmentsTotal}...`;
          } else {
            progress = 90;
            currentStep = 'Fusion des segments en cours...';
          }
        } else if (response.data.status === 'completed') {
          progress = 100;
          currentStep = 'Terminé !';
        }
        
        const jobStatus = {
          status: response.data.status,
          progress: progress,
          current_step: currentStep,
          video_url: response.data.video_url,
          error: response.data.error,
          custom_mode: response.data.custom_mode
        };
        
        setJobStatus(jobStatus);

        if (response.data.status === 'completed' || response.data.status === 'failed') {
          console.log('✅ Job finished:', response.data.status);
          clearInterval(interval);
          setLoading(false);
        }
      } catch (err) {
        console.error('Erreur polling:', err);
        
        if (err.response?.status === 404) {
          clearInterval(interval);
          setLoading(false);
          setError('Job introuvable. Veuillez réessayer.');
          setStep(1);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  };

  const reset = () => {
    setStep(1);
    setImageFile(null);
    setImagePreview(null);
    setProductName('');
    setDescription('');
    setCustomMode(false);
    setGender('any');
    setPrice('');
    setCompanyName('');
    setSlogan('');
    setGrokImages([null, null]);
    setGrokImagePreviews([null, null]);
    setGrokPrompts(['', '']);
    setJobStatus(null);
    setError(null);
  };

  // ✅ CALCUL STATS STOCKAGE
  const totalStoredVideos = standardVideos.length + grokVideos.length;
  const storagePercentage = (totalStoredVideos / MAX_STORED_VIDEOS) * 100;
  const isStorageFull = totalStoredVideos >= MAX_STORED_VIDEOS;

  if (view === 'credits') {
      return (
        <CreditsPurchase 
          onBack={() => {
            setView('dashboard');
          }} 
        />
      );
    }
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow hover:shadow-md transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour au Dashboard
        </button>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-12 h-12 text-pink-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              TeckiA Pubs Vidéos PRO
            </h1>
            <Sparkles className="w-8 h-8 text-orange-500 ml-3" />
          </div>
          <p className="text-lg text-gray-600">
            Vidéos publicitaires ultra-professionnelles avec IA
          </p>
          <button
                onClick={() => setView('credits')}
                className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-purple-600 from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 text-yellow-300 border border-yellow-500/30 transition-all hover:scale-105 text-sm md:text-base group"
              >
                <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                <span className="hidden sm:inline">Acheter Crédits</span>
                <span className="sm:hidden">Crédits</span>
              </button>
        </div>

        {/* ========== CRÉDITS DISPONIBLES ========== */}
        <CreditsBanner serviceId="video_ads" onBuy={() => setView('credits')} />

        {/* ========== TABS PRINCIPAUX ========== */}
        <div className="mb-6 flex gap-2 border-b-2 border-gray-200 bg-white rounded-t-xl px-4">
          <button
            onClick={() => setActiveMainTab('create')}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeMainTab === 'create'
                ? 'text-pink-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Video className="w-5 h-5 inline mr-2" />
            Créer une vidéo
            {activeMainTab === 'create' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-600 to-orange-600 rounded-t" />
            )}
          </button>

          <button
            onClick={() => {
              setActiveMainTab('my-videos');
              loadMyVideos();
              loadQuota();
            }}
            className={`px-6 py-3 font-semibold transition-all relative ${
              activeMainTab === 'my-videos'
                ? 'text-pink-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Film className="w-5 h-5 inline mr-2" />
            Mes Vidéos {totalStoredVideos > 0 && `(${totalStoredVideos}/${MAX_STORED_VIDEOS})`}
            {activeMainTab === 'my-videos' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-600 to-orange-600 rounded-t" />
            )}
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          
          {/* ========== ONGLET CRÉATION ========== */}
          {activeMainTab === 'create' && (
            <>
              {/* Progress bar */}
              <div className="bg-gradient-to-r from-pink-600 to-orange-600 p-6">
                <div className="flex items-center justify-between max-w-2xl mx-auto">
                  {[1, 2, 3].map((num) => (
                    <React.Fragment key={num}>
                      <div className="flex flex-col items-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg
                          ${step >= num ? 'bg-white text-pink-600' : 'bg-pink-400 text-white'}`}>
                          {num}
                        </div>
                        <span className="text-white text-sm mt-2 font-medium">
                          {num === 1 ? 'Image' : num === 2 ? 'Configuration' : 'Génération'}
                        </span>
                      </div>
                      {num < 3 && (
                        <div className={`flex-1 h-1 mx-4 ${step > num ? 'bg-white' : 'bg-pink-400'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="p-8">
                
                {/* ✅ ALERTE STOCKAGE PLEIN */}
                {isStorageFull && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                    <div className="flex items-start">
                      <Archive className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-red-800 mb-1">🚫 Stockage saturé !</p>
                        <p className="text-sm text-red-700 mb-2">
                          Vous avez atteint la limite de <strong>{MAX_STORED_VIDEOS} vidéos stockées</strong>.
                        </p>
                        <p className="text-sm text-red-600">
                          👉 Supprimez des anciennes vidéos dans l'onglet <strong>"Mes Vidéos"</strong> pour en créer de nouvelles.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-800">Erreur</p>
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  </div>
                )}

                {/* STEP 1: Upload Image */}
                {step === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <Package className="w-16 h-16 text-pink-600 mx-auto mb-3" />
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        Uploadez l'image de votre produit
                      </h2>
                      <p className="text-gray-600">
                        Photo haute qualité recommandée (min. 1920x1080px)
                      </p>
                    </div>

                    <div className="border-3 border-dashed border-pink-300 rounded-2xl p-12 text-center bg-pink-50/50 hover:bg-pink-50 transition">
                      {imagePreview ? (
                        <div className="space-y-4">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="max-h-96 mx-auto rounded-lg shadow-xl"
                          />
                          <div className="flex gap-3 justify-center">
                            <button
                              onClick={() => {
                                setImageFile(null);
                                setImagePreview(null);
                                setGrokImages([null, null]);
                                setGrokImagePreviews([null, null]);
                              }}
                              className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                            >
                              Changer l'image
                            </button>
                            <button
                              onClick={() => setStep(2)}
                              className="px-8 py-2 bg-gradient-to-r from-pink-600 to-orange-600 text-white rounded-lg hover:from-pink-700 hover:to-orange-700 transition font-medium"
                            >
                              Continuer →
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="cursor-pointer block">
                          <Upload className="w-20 h-20 mx-auto text-pink-400 mb-4" />
                          <p className="text-xl font-semibold text-gray-700 mb-2">
                            Cliquez pour uploader
                          </p>
                          <p className="text-sm text-gray-500">
                            PNG, JPG, JPEG ou WEBP • Max 10MB
                          </p>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                )}

                {/* STEP 2: Configuration */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <Sparkles className="w-16 h-16 text-pink-600 mx-auto mb-3" />
                      <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        Configuration Vidéo PRO
                      </h2>
                      <p className="text-gray-600">
                        Vidéo publicitaire professionnelle
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-pink-100 to-orange-100 rounded-lg p-4">
                      <img 
                        src={imagePreview} 
                        alt="Product" 
                        className="h-48 mx-auto rounded-lg shadow-md"
                      />
                    </div>

                    <div className="space-y-5">
                      
                      {/* CHOIX MODE */}
                      <div className="border-2 border-pink-200 rounded-lg p-4 bg-gradient-to-br from-pink-50 to-orange-50">
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          🎨 Mode de création
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div
                            onClick={() => setCustomMode(false)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                              !customMode
                                ? 'border-pink-600 bg-white shadow-lg'
                                : 'border-gray-200 hover:border-pink-300 bg-white/50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Sparkles className={`w-6 h-6 ${!customMode ? 'text-pink-600' : 'text-gray-400'}`} />
                              {!customMode && <CheckCircle className="w-5 h-5 text-pink-600" />}
                            </div>
                            <h3 className="font-bold text-gray-800 mb-1">🚀 Mode PRO Auto</h3>
                            <p className="text-xs text-gray-600">
                              Prompts professionnels générés automatiquement avec CTA, prix, slogan
                            </p>
                          </div>

                          <div
                            onClick={() => setCustomMode(true)}
                            className={`p-4 rounded-lg border-2 cursor-pointer transition ${
                              customMode
                                ? 'border-purple-600 bg-white shadow-lg'
                                : 'border-gray-200 hover:border-purple-300 bg-white/50'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <Wand2 className={`w-6 h-6 ${customMode ? 'text-purple-600' : 'text-gray-400'}`} />
                              {customMode && <CheckCircle className="w-5 h-5 text-purple-600" />}
                            </div>
                            <h3 className="font-bold text-gray-800 mb-1">✍️ Mode Personnalisé</h3>
                            <p className="text-xs text-gray-600">
                              Contrôle total sur les prompts des 2 segments
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Nom du produit */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Nom du produit *
                        </label>
                        <input
                          type="text"
                          value={productName}
                          onChange={(e) => setProductName(e.target.value)}
                          placeholder="Ex: Robe élégante rouge, Smartphone Galaxy..."
                          className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                        />
                      </div>

                      {/* MODE PRO AUTO */}
                      {!customMode && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Description du produit *
                            </label>
                            <textarea
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              placeholder="Ex: Élégante, confortable, parfaite pour toutes occasions..."
                              rows={3}
                              className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition resize-none"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <User className="w-4 h-4 mr-2 text-pink-600" />
                              Personne dans la vidéo
                            </label>
                            <div className="grid grid-cols-3 gap-3">
                              {[
                                { value: 'male', label: '👨 Homme', icon: '👨' },
                                { value: 'female', label: '👩 Femme', icon: '👩' },
                                { value: 'any', label: '👤 Peu importe', icon: '👤' }
                              ].map(option => (
                                <div
                                  key={option.value}
                                  onClick={() => setGender(option.value)}
                                  className={`p-3 rounded-lg border-2 cursor-pointer transition text-center ${
                                    gender === option.value
                                      ? 'border-pink-600 bg-pink-50'
                                      : 'border-gray-200 hover:border-pink-300'
                                  }`}
                                >
                                  <div className="text-2xl mb-1">{option.icon}</div>
                                  <p className="text-xs font-medium text-gray-700">
                                    {option.label.replace(option.icon + ' ', '')}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                              Prix (optionnel - affiché dans segment 2)
                            </label>
                            <input
                              type="number"
                              value={price}
                              onChange={(e) => setPrice(e.target.value)}
                              placeholder="50000"
                              className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                            />
                            <p className="text-xs text-gray-500 mt-1">Si rempli, sera affiché en doré dans le CTA final</p>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <Building className="w-4 h-4 mr-2 text-blue-600" />
                              Nom de l'entreprise (optionnel)
                            </label>
                            <input
                              type="text"
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              placeholder="TeckiA Shop"
                              className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                            />
                            <p className="text-xs text-gray-500 mt-1">Affiché de manière prestigieuse dans le CTA</p>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <MessageSquare className="w-4 h-4 mr-2 text-purple-600" />
                              Slogan (optionnel)
                            </label>
                            <input
                              type="text"
                              value={slogan}
                              onChange={(e) => setSlogan(e.target.value)}
                              placeholder="La qualité à votre portée !"
                              className="w-full px-4 py-3 border-2 border-pink-200 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition"
                            />
                            <p className="text-xs text-gray-500 mt-1">La personne prononcera ce slogan dans la vidéo</p>
                          </div>
                        </>
                      )}

                      {/* MODE PERSONNALISÉ */}
                      {customMode && (
                        <>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Description (optionnel)
                            </label>
                            <textarea
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                              placeholder="Description générale du produit..."
                              rows={2}
                              className="w-full px-4 py-3 border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition resize-none"
                            />
                          </div>

                          <div className="border-2 border-purple-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-pink-50">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">
                              📸 Images pour les 2 segments (optionnel)
                            </label>
                            <p className="text-xs text-gray-600 mb-3">
                              Uploadez 2 images différentes OU laissez vide pour réutiliser l'image principale
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                              {[0, 1].map((index) => (
                                <div key={index} className="border-2 border-dashed border-purple-200 rounded-lg p-4">
                                  <p className="text-xs font-bold text-gray-700 mb-2">Segment {index + 1}</p>
                                  {grokImagePreviews[index] ? (
                                    <div className="space-y-2">
                                      <img 
                                        src={grokImagePreviews[index]} 
                                        alt={`Segment ${index + 1}`} 
                                        className="w-full h-24 object-cover rounded"
                                      />
                                      <button
                                        onClick={() => {
                                          const newImages = [...grokImages];
                                          newImages[index] = null;
                                          setGrokImages(newImages);
                                          const newPreviews = [...grokImagePreviews];
                                          newPreviews[index] = null;
                                          setGrokImagePreviews(newPreviews);
                                        }}
                                        className="w-full text-xs px-2 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                      >
                                        Supprimer
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="cursor-pointer block">
                                      <div className="bg-purple-50 rounded p-3 text-center hover:bg-purple-100 transition">
                                        <Upload className="w-6 h-6 mx-auto text-purple-400 mb-1" />
                                        <p className="text-xs text-gray-600">Cliquez</p>
                                      </div>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleGrokImageUpload(index, e)}
                                        className="hidden"
                                      />
                                    </label>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="border-2 border-purple-200 rounded-lg p-4 bg-gradient-to-br from-purple-50 to-pink-50">
                            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
                              <Wand2 className="w-5 h-5 mr-2 text-purple-600" />
                              Prompts des 2 segments *
                            </label>
                            <div className="space-y-3">
                              {[0, 1].map((index) => (
                                <div key={index}>
                                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                                    Segment {index + 1} {index === 0 ? '(Début)' : '(Fin avec CTA)'}
                                  </label>
                                  <textarea
                                    value={grokPrompts[index]}
                                    onChange={(e) => {
                                      const newPrompts = [...grokPrompts];
                                      newPrompts[index] = e.target.value;
                                      setGrokPrompts(newPrompts);
                                    }}
                                    placeholder={index === 0 ? 
                                      'Ex: Personne élégante portant le produit dans un décor luxueux...' :
                                      'Ex: Personne souriante avec le produit, affichage du prix et nom entreprise...'
                                    }
                                    rows={3}
                                    className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition resize-none text-sm"
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="mt-3 text-xs text-purple-600 flex items-start">
                              <Sparkles className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                              <span>
                                Mode avancé: vous contrôlez entièrement les prompts
                              </span>
                            </p>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex gap-4 pt-4">
                      <button
                        onClick={() => setStep(1)}
                        className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium flex items-center justify-center"
                      >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Retour
                      </button>
                      <button
                        onClick={generateVideo}
                        disabled={loading || !productName || (!customMode && !description) || (customMode && (!grokPrompts[0] || !grokPrompts[1])) || isStorageFull}
                        className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-600 to-orange-600 text-white rounded-lg hover:from-pink-700 hover:to-orange-700 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {loading ? (
                          <>
                            <Clock className="w-5 h-5 mr-2 animate-spin" />
                            Génération...
                          </>
                        ) : isStorageFull ? (
                          <>
                            <XCircle className="w-5 h-5 mr-2" />
                            Stockage saturé
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            {customMode ? 'Générer Personnalisé 🎨' : 'Générer PRO 🚀'}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* STEP 3: Generation Progress */}
                {step === 3 && jobStatus && (
                  <div className="space-y-6">
                    
                    {jobStatus.status === 'processing' && (
                      <div>
                        <div className="text-center mb-6">
                          <div className="inline-flex items-center justify-center w-20 h-20 bg-pink-100 rounded-full mb-4">
                            <Video className="w-10 h-10 text-pink-600 animate-pulse" />
                          </div>
                          <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            🤖 Création vidéo {jobStatus.custom_mode ? 'personnalisée' : 'PRO'}...
                          </h2>
                          <p className="text-gray-600">
                            ⏱️ Temps estimé : ~60 secondes (2 segments)
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                              {jobStatus.current_step}
                            </span>
                            <span className="text-sm font-bold text-pink-600">
                              {jobStatus.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-pink-600 to-orange-600 transition-all duration-500 rounded-full"
                              style={{ width: `${jobStatus.progress}%` }}
                            />
                          </div>
                        </div>

                        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <Sparkles className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-blue-800">
                              <p className="font-medium mb-1">
                                Processus en cours :
                              </p>
                              <ul className="space-y-1 text-blue-700">
                                <li>• Génération parallèle des 2 segments</li>
                                <li>• {jobStatus.custom_mode ? 'Application de vos prompts personnalisés' : 'Optimisation automatique des prompts PRO'}</li>
                                <li>• Découpe des 2 premières secondes de chaque segment</li>
                                <li>• Fusion fluide des segments (2 × 4s = 8s réel)</li>
                                <li>• Qualité cinématographique ultra-réaliste</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {jobStatus.status === 'completed' && (
                      <div>
                        <div className="text-center mb-6">
                          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                          </div>
                          <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            🎉 Votre vidéo est prête !
                          </h2>
                          <p className="text-gray-600">
                            Vidéo {jobStatus.custom_mode ? 'personnalisée' : 'PRO'} de 10s (2 segments)
                          </p>
                        </div>

                        <div className="bg-black rounded-xl overflow-hidden shadow-2xl mb-6">
                          <video
                            controls
                            className="w-full"
                            src={jobStatus.video_url}
                          >
                            Votre navigateur ne supporte pas la lecture vidéo.
                          </video>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <a
                            href={jobStatus.video_url}
                            download
                            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold flex items-center justify-center"
                          >
                            <Download className="w-5 h-5 mr-2" />
                            Télécharger
                          </a>
                          <button
                            onClick={reset}
                            className="px-6 py-3 bg-gradient-to-r from-pink-600 to-orange-600 text-white rounded-lg hover:from-pink-700 hover:to-orange-700 transition font-semibold flex items-center justify-center"
                          >
                            <Video className="w-5 h-5 mr-2" />
                            Nouvelle Vidéo
                          </button>
                        </div>
                      </div>
                    )}

                    {jobStatus.status === 'failed' && (
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
                          <AlertCircle className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                          Erreur lors de la génération
                        </h2>
                        <p className="text-red-600 mb-6">
                          {jobStatus.error || 'Une erreur est survenue'}
                        </p>
                        <button
                          onClick={reset}
                          className="px-8 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition font-semibold"
                        >
                          Réessayer
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ========== ONGLET MES VIDÉOS ========== */}
          {activeMainTab === 'my-videos' && (
            <div className="p-8">
              
              {/* ✅ INDICATEUR STOCKAGE */}
              <div className={`mb-6 p-4 rounded-xl border-2 ${
                isStorageFull 
                  ? 'bg-red-50 border-red-300' 
                  : storagePercentage >= 80 
                  ? 'bg-orange-50 border-orange-300' 
                  : 'bg-blue-50 border-blue-300'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Archive className={`w-5 h-5 ${
                      isStorageFull ? 'text-red-600' : 
                      storagePercentage >= 80 ? 'text-orange-600' : 
                      'text-blue-600'
                    }`} />
                    <h3 className="font-bold text-gray-800">Espace de stockage</h3>
                  </div>
                  <span className={`text-lg font-bold ${
                    isStorageFull ? 'text-red-600' : 
                    storagePercentage >= 80 ? 'text-orange-600' : 
                    'text-blue-600'
                  }`}>
                    {totalStoredVideos} / {MAX_STORED_VIDEOS}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      isStorageFull ? 'bg-red-600' : 
                      storagePercentage >= 80 ? 'bg-orange-500' : 
                      'bg-blue-600'
                    }`}
                    style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                  />
                </div>
                {isStorageFull && (
                  <p className="text-sm text-red-700 mt-2">
                    🚫 <strong>Limite atteinte !</strong> Supprimez des vidéos pour en créer de nouvelles.
                  </p>
                )}
                {!isStorageFull && storagePercentage >= 80 && (
                  <p className="text-sm text-orange-700 mt-2">
                    ⚠️ Bientôt saturé ! Il reste {MAX_STORED_VIDEOS - totalStoredVideos} emplacement{MAX_STORED_VIDEOS - totalStoredVideos > 1 ? 's' : ''}.
                  </p>
                )}
              </div>
              
              {/* Quota */}
              {quota && quota.has_subscription && (
                <div className="bg-gradient-to-r from-pink-50 to-orange-50 rounded-xl p-6 border-2 border-pink-200 mb-6">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Sparkles className="w-5 h-5 mr-2 text-pink-600" />
                    Votre quota vidéo
                  </h3>
                  <div className="bg-white rounded-lg p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">🤖 TeckiA Generation</span>
                      <Sparkles className="w-5 h-5 text-pink-600" />
                    </div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-pink-600">{quota.remaining_grok}</span>
                      <span className="text-gray-500 text-xl">/ {quota.max_videos_grok}</span>
                    </div>
                    <div className="text-sm text-gray-600 mb-3">
                      {quota.used_videos_grok} vidéo{quota.used_videos_grok > 1 ? 's' : ''} utilisée{quota.used_videos_grok > 1 ? 's' : ''}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-pink-600 to-orange-600 h-3 rounded-full transition-all"
                        style={{ width: `${(quota.remaining_grok / quota.max_videos_grok) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                  <Sparkles className="w-6 h-6 mr-2 text-pink-600" />
                  Mes vidéos ({grokVideos.length})
                </h3>
              </div>

              {/* Liste vidéos */}
              {loadingVideos ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-pink-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement...</p>
                </div>
              ) : (
                <>
                  {grokVideos.length === 0 ? (
                    <div className="text-center py-12 bg-gradient-to-br from-pink-50 to-orange-50 rounded-xl">
                      <Sparkles className="w-16 h-16 text-pink-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">
                        Aucune vidéo
                      </h3>
                      <p className="text-gray-500">
                        Créez votre première vidéo pour la voir apparaître ici
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {grokVideos.map((video) => (
                        <div
                          key={video.job_id}
                          className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all overflow-hidden border-2 border-pink-200"
                        >
                          {video.status === 'completed' && video.video_url ? (
                            <div className="relative aspect-video bg-black">
                              <video
                                src={video.video_url}
                                className="w-full h-full object-contain"
                                controls
                              />
                            </div>
                          ) : (
                            <div className="aspect-video bg-gradient-to-br from-pink-100 to-orange-100 flex items-center justify-center">
                              {video.status === 'processing' ? (
                                <div className="text-center">
                                  <Clock className="w-12 h-12 text-pink-400 mx-auto mb-2 animate-spin" />
                                  <p className="text-sm text-pink-600">En cours...</p>
                                </div>
                              ) : video.status === 'failed' ? (
                                <div className="text-center">
                                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-2" />
                                  <p className="text-sm text-red-600">Échec</p>
                                </div>
                              ) : (
                                <Clock className="w-12 h-12 text-pink-400 animate-pulse" />
                              )}
                            </div>
                          )}

                          <div className="p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800 text-sm line-clamp-1">
                                  {video.product_name || video.prompt?.slice(0, 50) || 'Vidéo 10s'}
                                </h4>
                                <p className="text-xs text-pink-600 font-medium mt-1">
                                  10s • 2 segments {video.custom_mode && '• Personnalisé'}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                video.status === 'completed' ? 'bg-green-100 text-green-700' :
                                video.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {video.status === 'completed' ? 'Terminé' :
                                 video.status === 'failed' ? 'Échec' :
                                 'En cours'}
                              </span>
                            </div>

                            <p className="text-xs text-gray-500 mb-3">
                              {video.created_date || new Date(video.created_at * 1000).toLocaleString('fr-FR')}
                            </p>

                            <div className="flex gap-2">
                              {video.status === 'completed' && (
                                <>
                                  <a
                                    href={video.video_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition text-xs font-medium text-center flex items-center justify-center gap-1"
                                  >
                                    <Play className="w-3 h-3" />
                                    Voir
                                  </a>
                                  <a
                                    href={video.video_url}
                                    download
                                    className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-xs font-medium text-center flex items-center justify-center gap-1"
                                  >
                                    <Download className="w-3 h-3" />
                                    DL
                                  </a>
                                </>
                              )}
                              <button
                                onClick={() => deleteVideoFromList('grok', video.job_id)}
                                className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition text-xs"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoAdCreatorPro;